// Scratchpad (Agent Builder) changelog entries.
//
// SOURCE OF TRUTH for the rendered UI. Keep in sync with
// docs/features/scratchpad/CHANGELOG.md (the human-readable mirror).
//
// Add a new entry at the TOP of the array whenever a user-facing change is
// shipped to the Scratchpad / Agent Builder feature.

// Feature area within the Scratchpad / Agent Builder umbrella. Used to filter
// the changelog by the tab selected in the ChangelogPanel UI. Use "general"
// for entries that don't clearly belong to a single area.
export type ChangelogArea =
  | "agent-builder"
  | "ontology"
  | "transforms"
  | "data-sources"
  | "general";

export interface ChangelogItem {
  area: ChangelogArea;
  text: string;
}

export interface ChangelogEntry {
  date: string; // ISO date, e.g. "2026-05-24"
  items: ChangelogItem[];
}

export const scratchpadChangelog: ChangelogEntry[] = [
  {
    date: "2026-05-24",
    items: [
      {
        area: "agent-builder",
        text: 'Renamed the "Run with Claude" button to simply "Run".',
      },
      {
        area: "agent-builder",
        text: "Run no longer needs a separate user message — the agent prompt is the whole prompt. The User message field has been removed from the preview pane.",
      },
      {
        area: "agent-builder",
        text: "Run with Claude now caches the skill's system prompt between runs, lowering cost and latency on repeated runs; the usage readout shows cached vs. fresh tokens.",
      },
      {
        area: "agent-builder",
        text: "Fixed version history in the Agent Builder: the History button is back in the editor and the version list refreshes after each save.",
      },
      {
        area: "data-sources",
        text: "Data Sources now show the uploader's name instead of a user id.",
      },
      {
        area: "data-sources",
        text: "Data Sources upload timestamp now includes the time of day.",
      },
      { area: "general", text: "Polished the changelog panel layout for easier reading." },
      {
        area: "agent-builder",
        text: "Changelog panel added to the left column of Agent Builder.",
      },
      {
        area: "ontology",
        text: "Enum value chip editor: edit allowed values inline on property type rows.",
      },
      {
        area: "ontology",
        text: "api_name naming convention enforced: Object Type API names are immutable after first save.",
      },
      {
        area: "transforms",
        text: "Transforms promoted to a first-class nav destination (top-level Transforms tab).",
      },
      {
        area: "agent-builder",
        text: "Widened name inputs in the tree and frontmatter form for long agent names.",
      },
      {
        area: "general",
        text: "Product documentation (PRODUCT.md) published covering the full Agent Builder feature.",
      },
    ],
  },
  {
    date: "2026-05-20",
    items: [
      {
        area: "agent-builder",
        text: "Drag-to-reorder agents, skills, and subskills in the file tree.",
      },
      {
        area: "ontology",
        text: "Property descriptions: free-text description field on each property type.",
      },
      {
        area: "ontology",
        text: "Kind badges on property type rows indicate the data kind (string, number, date, enum, …).",
      },
      {
        area: "agent-builder",
        text: "Add Skill and Add Subskill affordances visible on hover in the tree.",
      },
      {
        area: "agent-builder",
        text: "Left sidebar cleanup: tightened spacing, consistent icon sizing.",
      },
    ],
  },
  {
    date: "2026-05-15",
    items: [
      {
        area: "transforms",
        text: "Phase 2 complete: Transforms editor with CodeMirror JS editor, dry-run preview, and run history.",
      },
      {
        area: "ontology",
        text: "Object Type editor: create, edit, and soft-delete Object Types with typed properties.",
      },
      {
        area: "data-sources",
        text: "Data ingestion wizard (/data-sources): upload CSV, map columns, preview coercion, materialize.",
      },
      {
        area: "data-sources",
        text: "Header-signature auto-apply: repeated uploads with the same column headers skip the mapping step.",
      },
      {
        area: "agent-builder",
        text: "Editor retrofit: trigger-rule field is now a typed dropdown when an Object Type is bound.",
      },
    ],
  },
  {
    date: "2026-05-10",
    items: [
      { area: "agent-builder", text: "Phase 1 complete: full Agent Skills editor is live." },
      {
        area: "agent-builder",
        text: "Run with Claude: stream a live response from Claude directly in the preview pane.",
      },
      {
        area: "agent-builder",
        text: "History drawer: browse and restore previous versions of any agent file.",
      },
      {
        area: "agent-builder",
        text: "Optimistic locking: concurrent edits surface a stale-version dialog instead of silently overwriting.",
      },
      {
        area: "agent-builder",
        text: "Preview pane: composed prompt view, validation list, test context, and user message inputs.",
      },
    ],
  },
  {
    date: "2026-05-05",
    items: [
      {
        area: "agent-builder",
        text: "Initial scaffolding: /scratchpad route, three-panel resizable layout (tree / editor / preview).",
      },
      {
        area: "agent-builder",
        text: "Agent file tree with create, rename, duplicate, and soft-delete.",
      },
      {
        area: "agent-builder",
        text: "Three-level depth enforcement: Agent → Skill → Subskill.",
      },
      {
        area: "general",
        text: "Admin-only access gate: non-admin users are redirected to the dashboard.",
      },
    ],
  },
];
